/**
 * ShiftHistoryPage — Phase 4.
 *
 * Admin/owner view of all shifts. Filter by branch/cashier/status/date/close-method.
 * Each row shows shift summary; clicking opens X-Report modal. For OPEN shifts,
 * admins/managers can force-close via the embedded ForceCloseDialog.
 *
 * Cashiers reaching this page see only their own shifts (server-side restriction).
 *
 * Renders nothing meaningful when the feature flag is OFF — shows an empty
 * state with a hint to enable the feature in Settings.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  ClipboardCheck, AlertTriangle, X, KeyRound, RefreshCw, ShieldAlert,
  TrendingUp, TrendingDown, Minus, Users, Clock, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

import VarianceTrendChart from '../components/shifts/VarianceTrendChart';

const STATUS_BADGE = (s, method) => {
  if (s === 'open') return <Badge className="bg-amber-100 text-amber-700">open</Badge>;
  if (method === 'manager_force') return <Badge className="bg-orange-100 text-orange-700">force</Badge>;
  if (method === 'auto_midnight') return <Badge className="bg-red-100 text-red-700">auto-midnight</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-700">closed</Badge>;
};

export default function ShiftHistoryPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featureEnabled, setFeatureEnabled] = useState(null);

  const [branches, setBranches] = useState([]);
  const [filterBranch, setFilterBranch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCloseMethod, setFilterCloseMethod] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterCashier, setFilterCashier] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [xReportShift, setXReportShift] = useState(null);
  const [xReport, setXReport] = useState(null);
  const [forceCloseShift, setForceCloseShift] = useState(null);

  const role = (user?.role || '').toLowerCase();
  const isAdmin = role === 'admin' || role === 'owner' || user?.is_super_admin;

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/shifts/feature-status');
      setFeatureEnabled(!!res.data.enabled);
    } catch {
      setFeatureEnabled(false);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const res = await api.get('/branches');
      setBranches(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterBranch) params.branch_id = filterBranch;
      if (filterStatus) params.status = filterStatus;
      if (filterCloseMethod) params.close_method = filterCloseMethod;
      if (filterFrom) params.date_from = filterFrom;
      if (filterTo) params.date_to = filterTo;
      const res = await api.get('/shifts', { params });
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [filterBranch, filterStatus, filterCloseMethod, filterFrom, filterTo]);

  useEffect(() => { fetchStatus(); fetchBranches(); }, [fetchStatus, fetchBranches]);
  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  const openXReport = async (shift) => {
    setXReportShift(shift);
    setXReport(null);
    try {
      const res = await api.get(`/shifts/${shift.shift_id}/x-report`);
      setXReport(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load X-Report');
    }
  };

  // ── Derived stats ──────────────────────────────────────────────────────────
  const closedItems = useMemo(() => items.filter(s => s.status === 'closed'), [items]);
  const balanced = useMemo(() => closedItems.filter(s => Number(s.cash_variance || 0) === 0), [closedItems]);
  const overShifts = useMemo(() => closedItems.filter(s => Number(s.cash_variance || 0) > 0), [closedItems]);
  const shortShifts = useMemo(() => closedItems.filter(s => Number(s.cash_variance || 0) < 0), [closedItems]);
  const totalOver = useMemo(() => overShifts.reduce((sum, s) => sum + Number(s.cash_variance || 0), 0), [overShifts]);
  const totalShort = useMemo(() => shortShifts.reduce((sum, s) => sum + Number(s.cash_variance || 0), 0), [shortShifts]);
  const netVariance = useMemo(() => closedItems.reduce((sum, s) => sum + Number(s.cash_variance || 0), 0), [closedItems]);

  // Unique cashiers for filter
  const cashierNames = useMemo(() => [...new Set(items.map(s => s.cashier_name).filter(Boolean))], [items]);

  // Filtered view (client-side cashier filter only)
  const displayed = useMemo(() => {
    if (!filterCashier) return items;
    return items.filter(s => s.cashier_name === filterCashier);
  }, [items, filterCashier]);

  if (featureEnabled === false) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="shift-history-disabled">
        <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-center">
          <ShieldAlert size={28} className="text-amber-700 mx-auto mb-2" />
          <h3 className="text-base font-semibold text-amber-900">Multi-Cashier Shifts is disabled</h3>
          <p className="text-sm text-amber-800 mt-1">Enable it in Settings → Shifts to start tracking per-cashier accountability.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="shift-history-page">
      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <header className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[24px] font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}>
            <Users size={20} className="text-emerald-700" />
            Team Shifts
          </h1>
          <p className="text-[13px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            {isAdmin ? 'Per-cashier accountability, variance history & X-Reports.' : 'Your shift performance history.'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchShifts} disabled={loading} data-testid="refresh-shifts">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </Button>
      </header>

      {/* ── Stats Cards ───────────────────────────────────────────────────── */}
      {closedItems.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
          <StatCard
            label="Total Shifts"
            value={items.length}
            sub={`${closedItems.length} closed`}
            icon={<ClipboardCheck size={16} />}
            color="slate"
          />
          <StatCard
            label="Balanced"
            value={balanced.length}
            sub={`${closedItems.length > 0 ? Math.round(balanced.length / closedItems.length * 100) : 0}% of closed`}
            icon={<Minus size={16} />}
            color="emerald"
          />
          <StatCard
            label="Over"
            value={overShifts.length}
            sub={overShifts.length > 0 ? `+₱${fmtPHP(totalOver)} total` : 'None'}
            icon={<TrendingUp size={16} />}
            color="blue"
          />
          <StatCard
            label="Short"
            value={shortShifts.length}
            sub={shortShifts.length > 0 ? `-₱${fmtPHP(Math.abs(totalShort))} total` : 'None'}
            icon={<TrendingDown size={16} />}
            color="red"
          />
          <StatCard
            label="Net Variance"
            value={`${netVariance >= 0 ? '+' : ''}₱${fmtPHP(Math.abs(netVariance))}`}
            sub={netVariance === 0 ? 'Fully balanced' : netVariance > 0 ? 'Net over' : 'Net short'}
            icon={netVariance >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            color={netVariance === 0 ? 'emerald' : netVariance > 0 ? 'blue' : 'red'}
          />
          {items.some(s => s.status === 'open') && (
            <StatCard
              label="Open Shifts"
              value={items.filter(s => s.status === 'open').length}
              sub="Currently active"
              icon={<Clock size={16} />}
              color="amber"
            />
          )}
        </div>
      )}

      {/* ── Variance Trend Chart ──────────────────────────────────────────── */}
      <div className="mb-5">
        <VarianceTrendChart branchId={filterBranch || undefined} />
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setFiltersOpen(f => !f)}
          className="flex items-center gap-2 text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors mb-2"
          data-testid="toggle-filters"
        >
          {filtersOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {filtersOpen ? 'Hide' : 'Show'} Filters
        </button>

        {filtersOpen && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            {isAdmin && (
              <div>
                <Label className="text-[11px] uppercase text-slate-500">Branch</Label>
                <Select value={filterBranch || 'all'} onValueChange={(v) => setFilterBranch(v === 'all' ? '' : v)}>
                  <SelectTrigger data-testid="filter-branch" className="h-8 text-xs mt-1"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[11px] uppercase text-slate-500">Cashier</Label>
              <Select value={filterCashier || 'all'} onValueChange={(v) => setFilterCashier(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-cashier" className="h-8 text-xs mt-1"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cashiers</SelectItem>
                  {cashierNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-slate-500">Status</Label>
              <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-status" className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-slate-500">Close Method</Label>
              <Select value={filterCloseMethod || 'all'} onValueChange={(v) => setFilterCloseMethod(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-close-method" className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="manager_force">Force</SelectItem>
                  <SelectItem value="auto_midnight">Auto-Midnight</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-slate-500">From</Label>
              <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="h-8 text-xs mt-1" data-testid="filter-from" />
            </div>
            <div>
              <Label className="text-[11px] uppercase text-slate-500">To</Label>
              <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="h-8 text-xs mt-1" data-testid="filter-to" />
            </div>
          </div>
        )}
      </div>

      {/* ── Shift Table ────────────────────────────────────────────────────── */}
      <div style={{ border: '1px solid var(--border-default)' }} className="rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F1F0ED', borderBottom: '1px solid var(--border-default)' }}>
            <tr className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
              <th className="text-left px-3 py-2.5">Shift</th>
              <th className="text-left px-3 py-2.5">Cashier</th>
              {isAdmin && <th className="text-left px-3 py-2.5">Branch</th>}
              <th className="text-left px-3 py-2.5">Opened</th>
              <th className="text-left px-3 py-2.5">Closed</th>
              <th className="text-center px-3 py-2.5">Status</th>
              <th className="text-right px-3 py-2.5">Float</th>
              <th className="text-right px-3 py-2.5">Expected</th>
              <th className="text-right px-3 py-2.5">Counted</th>
              <th className="text-right px-3 py-2.5">Variance</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="11" className="text-center text-slate-400 py-10">Loading…</td></tr>
            ) : displayed.length === 0 ? (
              <tr><td colSpan="11" className="text-center text-slate-400 py-10" data-testid="no-shifts-empty">No shifts found.</td></tr>
            ) : displayed.map((s) => {
              const v = Number(s.cash_variance || 0);
              const isOpen = s.status === 'open';
              const branchName = branches.find((b) => b.id === s.branch_id)?.name || s.branch_id?.slice(0, 6);
              return (
                <tr
                  key={s.shift_id}
                  className={`border-t border-slate-100 transition-colors hover:bg-slate-50/60 ${!isOpen && v !== 0 ? (v > 0 ? 'bg-blue-50/20' : 'bg-red-50/20') : ''}`}
                  data-testid={`shift-row-${s.shift_id}`}
                >
                  <td className="px-3 py-2.5 text-[11px]" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>{s.shift_id}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold flex items-center justify-center shrink-0">
                        {(s.cashier_name || '?')[0]}
                      </div>
                      <span className="font-medium text-slate-800 text-xs">{s.cashier_name}</span>
                    </div>
                  </td>
                  {isAdmin && <td className="px-3 py-2.5 text-[11px] text-slate-500">{branchName}</td>}
                  <td className="px-3 py-2.5 text-[11px] text-slate-600">{fmtTime(s.opened_at)}</td>
                  <td className="px-3 py-2.5 text-[11px] text-slate-600">{s.closed_at ? fmtTime(s.closed_at) : '—'}</td>
                  <td className="px-3 py-2.5 text-center">{STATUS_BADGE(s.status, s.close_method)}</td>
                  <td className="px-3 py-2.5 text-right text-xs" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>₱{fmtPHP(s.opening_float)}</td>
                  <td className="px-3 py-2.5 text-right text-xs" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>
                    {isOpen ? '—' : s.cash_expected != null ? `₱${fmtPHP(s.cash_expected)}` : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>
                    {isOpen ? '—' : s.cash_counted != null ? `₱${fmtPHP(s.cash_counted)}` : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {isOpen ? (
                      <span className="text-slate-400 text-xs">—</span>
                    ) : (
                      <VarianceBadge variance={v} closeMethod={s.close_method} />
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => openXReport(s)} data-testid={`view-${s.shift_id}`}>
                        X-Report
                      </Button>
                      {s.status === 'open' && isAdmin && (
                        <Button
                          size="sm"
                          className="h-7 text-xs px-2 bg-orange-600 hover:bg-orange-700"
                          onClick={() => setForceCloseShift(s)}
                          data-testid={`force-close-${s.shift_id}`}
                        >
                          Force
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 text-xs text-slate-400 flex items-center justify-between">
          <span>{displayed.length} shift{displayed.length === 1 ? '' : 's'}</span>
          {netVariance !== 0 && closedItems.length > 0 && (
            <span className={`font-medium ${netVariance > 0 ? 'text-blue-600' : 'text-red-600'}`}>
              Net variance: {netVariance > 0 ? '+' : ''}₱{fmtPHP(Math.abs(netVariance))}
            </span>
          )}
        </div>
      </div>

      {/* ── X-Report Modal ─────────────────────────────────────────────────── */}
      <Dialog open={!!xReportShift} onOpenChange={(o) => !o && (setXReportShift(null), setXReport(null))}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="x-report-modal">
          <DialogHeader>
            <DialogTitle>X-Report · {xReportShift?.shift_id}</DialogTitle>
            <DialogDescription>{xReportShift?.cashier_name}</DialogDescription>
          </DialogHeader>
          {!xReport ? <div className="text-sm text-slate-400 py-4 text-center">Loading…</div> : (
            <div className="space-y-3 text-sm">
              <Section title="Shift">
                <Row label="Status" value={STATUS_BADGE(xReport.status, xReport.close_method)} />
                <Row label="Opened" value={fmtTime(xReport.opened_at)} />
                <Row label="Closed" value={xReport.closed_at ? fmtTime(xReport.closed_at) : '—'} />
                <Row label="Opening float" value={`₱${fmtPHP(xReport.opening_float)}`} />
                {xReport.opening_authorized_by_name && (
                  <Row label="Authorized by" value={xReport.opening_authorized_by_name} />
                )}
              </Section>
              <Section title="Sales by Type">
                {Object.entries(xReport.sales_by_payment_type || {}).map(([t, v]) => (
                  <Row key={t} label={`${t} (${v.count})`} value={`₱${fmtPHP(v.total)}`} />
                ))}
                <Row label="Total gross sales" value={`₱${fmtPHP(xReport.total_gross_sales || 0)}`} bold />
              </Section>
              <Section title="Cash Reconciliation">
                <Row label="Opening float" value={`₱${fmtPHP(xReport.cash_breakdown?.opening_float || 0)}`} />
                <Row label="Cash sales" value={`₱${fmtPHP(xReport.cash_breakdown?.sales_cash || 0)}`} />
                <Row label="AR collections" value={`₱${fmtPHP(xReport.cash_breakdown?.ar_collections_cash || 0)}`} />
                <Row label="Drops" value={`₱${fmtPHP(xReport.cash_breakdown?.drops || 0)}`} />
                <Row label="Expected" value={`₱${fmtPHP(xReport.cash_expected || 0)}`} bold />
                {xReport.cash_counted != null && <Row label="Counted" value={`₱${fmtPHP(xReport.cash_counted)}`} bold />}
                {xReport.cash_variance != null && (
                  <div className="pt-1">
                    <VarianceBadge variance={xReport.cash_variance} closeMethod={xReport.close_method} large />
                  </div>
                )}
                {xReport.variance_reason && <Row label="Reason" value={xReport.variance_reason} />}
              </Section>
              {xReport.epay_grand_total > 0 && (
                <Section title="Digital / E-Pay">
                  {Object.entries(xReport.epay_totals || {}).map(([p, v]) => (
                    <Row key={p} label={p} value={`₱${fmtPHP(v)}`} />
                  ))}
                  <Row label="Total" value={`₱${fmtPHP(xReport.epay_grand_total)}`} bold />
                </Section>
              )}
              {(xReport.drops || []).length > 0 && (
                <Section title={`Drops (${xReport.drops.length})`}>
                  {xReport.drops.map((d) => (
                    <Row key={d.id} label={`${d.destination.replace('_', ' ')} · ${d.authorized_by_name}`} value={`₱${fmtPHP(d.amount)}`} />
                  ))}
                </Section>
              )}
              {xReport.manager_cosigner_name && (
                <div className="p-2.5 rounded bg-slate-50 border border-slate-200 text-xs text-slate-600 flex items-center gap-1.5">
                  <X size={11} className="text-emerald-600" />
                  Co-signed by <strong>{xReport.manager_cosigner_name}</strong>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => (setXReportShift(null), setXReport(null))}>Close</Button>
            <Button variant="outline" onClick={() => window.print()}>Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force-close modal */}
      <ForceCloseDialog
        shift={forceCloseShift}
        onClose={() => setForceCloseShift(null)}
        onForceClosed={() => { setForceCloseShift(null); fetchShifts(); }}
      />
    </div>
  );
}

function ForceCloseDialog({ shift, onClose, onForceClosed }) {
  const [cashCounted, setCashCounted] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [pin, setPin] = useState('');
  const [expected, setExpected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!shift) {
      setCashCounted(''); setReason(''); setNote(''); setPin(''); setExpected(null);
      return;
    }
    api.get(`/shifts/${shift.shift_id}/x-report`)
      .then((res) => setExpected(res.data?.cash_expected))
      .catch(() => setExpected(null));
  }, [shift]);

  if (!shift) return null;
  const counted = parseFloat(cashCounted) || 0;
  const variance = expected == null ? null : +(counted - expected).toFixed(2);

  const submit = async () => {
    if (!pin.trim()) { toast.error('Manager PIN required.'); return; }
    if (variance !== 0 && !reason.trim()) { toast.error('Variance reason required.'); return; }
    setSubmitting(true);
    try {
      await api.post(`/shifts/${shift.shift_id}/force-close`, {
        cash_counted: counted,
        variance_reason: reason.trim() || undefined,
        manager_pin: pin.trim(),
        note: note.trim() || undefined,
      });
      toast.success(`Shift ${shift.shift_id} force-closed.`);
      onForceClosed?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Force-close failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!shift} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="force-close-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-orange-600" />
            Force-Close Shift
          </DialogTitle>
          <DialogDescription>
            {shift.cashier_name}'s shift ({shift.shift_id}). This bypasses the cashier's own PIN.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-2.5 bg-slate-50 border border-slate-200 rounded text-xs flex justify-between">
            <span>Expected cash</span>
            <span className="font-semibold">{expected == null ? '…' : `₱${fmtPHP(expected)}`}</span>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fc-counted">Counted Cash (₱)</Label>
            <Input
              id="fc-counted" data-testid="fc-counted-input"
              type="number" min="0" step="0.01" value={cashCounted}
              onChange={(e) => setCashCounted(e.target.value)} autoFocus
            />
          </div>
          {variance != null && variance !== 0 && (
            <>
              <div className={`text-xs font-medium ${variance > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                Variance: {variance > 0 ? '+' : ''}₱{fmtPHP(Math.abs(variance))}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fc-reason">Reason for variance</Label>
                <Textarea
                  id="fc-reason" data-testid="fc-reason-input" rows={2}
                  value={reason} onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="fc-note">Note (cashier absence reason)</Label>
            <Input
              id="fc-note" data-testid="fc-note-input"
              placeholder="e.g., cashier left for emergency"
              value={note} onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fc-pin" className="flex items-center gap-1.5">
              <KeyRound size={13} /> Manager PIN
            </Label>
            <Input
              id="fc-pin" data-testid="fc-pin-input"
              type="password" placeholder="Required"
              value={pin} onChange={(e) => setPin(e.target.value)} autoComplete="off"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            data-testid="confirm-force-close"
            onClick={submit}
            disabled={submitting || !pin.trim() || !cashCounted}
            className="bg-orange-700 hover:bg-orange-800"
          >
            {submitting ? 'Closing…' : 'Force Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── New helpers ──────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon, color }) {
  const colors = {
    slate:   { bg: 'bg-slate-50',   border: 'border-slate-200',  text: 'text-slate-700',   icon: 'bg-slate-200 text-slate-600' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: 'bg-emerald-100 text-emerald-700' },
    blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',   text: 'text-blue-800',    icon: 'bg-blue-100 text-blue-700' },
    red:     { bg: 'bg-red-50',     border: 'border-red-200',    text: 'text-red-800',     icon: 'bg-red-100 text-red-700' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',  text: 'text-amber-800',   icon: 'bg-amber-100 text-amber-700' },
  };
  const c = colors[color] || colors.slate;
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} px-3 py-3`} data-testid={`stat-card-${color}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">{label}</span>
        <div className={`w-6 h-6 rounded-md ${c.icon} flex items-center justify-center`}>{icon}</div>
      </div>
      <p className={`text-xl font-bold  ${c.text}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function VarianceBadge({ variance, closeMethod, large }) {
  const v = Number(variance || 0);
  const isForce = closeMethod === 'manager_force';
  const isAuto = closeMethod === 'auto_midnight';
  if (v === 0 && !isForce && !isAuto) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${large ? 'text-xs' : 'text-[10px]'} bg-emerald-100 text-emerald-700`}>
        ₱0.00 ✓
      </span>
    );
  }
  const cls = v > 0 ? 'bg-blue-100 text-blue-700' : v < 0 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700';
  const label = isForce ? 'forced' : isAuto ? 'auto' : v > 0 ? 'over' : 'short';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${large ? 'text-xs' : 'text-[10px]'} ${cls}`}>
      {v !== 0 && (v > 0 ? '+' : '')}₱{fmtPHP(Math.abs(v))} {label}
    </span>
  );
}

// ── Existing helpers (preserved) ─────────────────────────────────────────────

function fmtPHP(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function Section({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <div className="bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">{title}</div>
      <div className="px-3 py-2 space-y-1">{children}</div>
    </div>
  );
}
function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`${bold ? 'font-semibold text-slate-900' : 'text-slate-800'}`}>{value}</span>
    </div>
  );
}
